import type {
  DataObjectType,
  DataObjectAppJSON,
  DataObjectFieldsSchema,
  DataObjectField,
  DataObjectFieldsSchemaAppJSON,
  DataObjectFieldAppJSON
} from "@certa/types/src/dataObjects.types";
import {
  reverseParseDataObject,
  reverseParseXRelationship,
  reverseParseXRollup
} from "./reverseParseDataObject";

/**
 * Reverse parse a single DataObjectFieldAppJSON to DataObjectField
 * Converts __studio_frozen to __isFrozen with lossless transformation
 * Note: DataObjectFieldAppJSON already has camelCase properties from DataObjectField
 */
const reverseParseDataObjectFieldAppJSON = (
  field: DataObjectFieldAppJSON
): DataObjectField => {
  const {
    indexed: isIndexed,
    uniqueItems: hasUniqueItems,
    "x-rollup": xRollup,
    "x-relationship": xRelationship,
    __studio_frozen: isStudioFrozen,
    ...restFieldProps
  } = field;

  // Create the result with proper type - fields are already in camelCase
  const result: DataObjectField = {
    ...restFieldProps
  };

  if (isStudioFrozen !== undefined) {
    result.__isFrozen = isStudioFrozen;
  } else {
    // Default value if not present
    result.__isFrozen = false;
  }

  if (isIndexed !== undefined) {
    result.isIndexed = isIndexed;
  }

  if (hasUniqueItems !== undefined) {
    result.hasUniqueItems = hasUniqueItems;
  }

  if (xRollup) {
    result["x-rollup"] = reverseParseXRollup(xRollup);
  }

  if (xRelationship) {
    result["x-relationship"] = reverseParseXRelationship(xRelationship);
  }

  return result;
};

/**
 * Reverse parse DataObjectFieldsSchemaAppJSON to DataObjectFieldsSchema
 */
const reverseParseDataObjectSchemaAppJSON = (
  schema: DataObjectFieldsSchemaAppJSON
): DataObjectFieldsSchema => {
  const properties: DataObjectFieldsSchema["properties"] = {};

  for (const [fieldTag, field] of Object.entries(schema.properties)) {
    properties[fieldTag] = reverseParseDataObjectFieldAppJSON(field);
  }

  return {
    properties,
    required: schema.required
  };
};

/**
 * Reverse parse DataObjectAppJSON to DataObjectType
 * Converts known properties from snake_case to camelCase while preserving unknown properties
 * for lossless transformation. Also handles studio-specific fields.
 */
export const reverseParseDataObjectAppJSON = (
  dataObject: DataObjectAppJSON
): DataObjectType => {
  const {
    tag,
    schema,
    ui_schema: uiSchema,
    translated_ui_schema: translatedUiSchema,
    filter_config: filterConfig,
    __studio_save_id: studioSaveId,
    __studio_description: studioDescription,
    ...unknownProps // Preserve any unknown properties
  } = dataObject;

  // First, create a temporary raw object to reuse reverseParseDataObject
  // We'll override the schema later with AppJSON-specific parsing
  const tempSchema = {
    properties: {},
    required: schema.required
  };

  const rawDataObject = {
    tag,
    schema: tempSchema,
    ui_schema: uiSchema,
    translated_ui_schema: translatedUiSchema,
    filter_config: filterConfig,
    ...unknownProps
  };

  // Use reverseParseDataObject to handle ui_schema, translated_ui_schema, filter_config
  const typeDataObject = reverseParseDataObject(rawDataObject);

  // Override schema with AppJSON-specific parsing
  const schemaType = reverseParseDataObjectSchemaAppJSON(schema);

  const result: DataObjectType = {
    ...typeDataObject,
    schema: schemaType
  };

  if (studioSaveId !== undefined) {
    result.__saveId = String(studioSaveId);
  }

  if (studioDescription !== undefined) {
    result.__description = studioDescription;
  }

  return result;
};
